import { useState, useEffect, useRef, useCallback } from 'react';
import { API_ORIGIN } from '../api/client';

const TEMPLATE_VARS = [
  { label: '고객사명', value: '{{고객사명}}' }, { label: '수신자', value: '{{수신자}}' },
  { label: '사업명', value: '{{사업명}}' },
  { label: '견적합계(한글,VAT포함)', value: '{{견적합계_한글_VAT포함}}' }, { label: '견적합계(한글,VAT미포함)', value: '{{견적합계_한글_VAT미포함}}' },
  { label: '견적합계(숫자,VAT포함)', value: '{{견적합계_숫자_VAT포함}}' }, { label: '견적합계(숫자,VAT미포함)', value: '{{견적합계_숫자_VAT미포함}}' },
  { label: '견적년월일', value: '{{견적년월일}}' }, { label: '견적유효기간', value: '{{견적유효기간}}' },
  { label: '최종사용자', value: '{{최종사용자}}' }, { label: '견적번호', value: '{{견적번호}}' },
  { label: '결제조건', value: '{{결제조건}}' }, { label: '납기', value: '{{납기}}' },
  { label: '무상하자보증기간', value: '{{무상하자보증기간}}' }, { label: '비고', value: '{{비고}}' },
  { label: '담당자 이름', value: '{{담당자_이름}}' }, { label: '담당자 직급', value: '{{담당자_직급}}' },
  { label: '담당자 휴대전화', value: '{{견적담당자_전화}}' }, { label: '담당자 이메일', value: '{{견적담당자_메일}}' },
];

const PREVIEW_SAMPLE = {
  '{{고객사명}}':'ABC Trading Co., Ltd.', '{{수신자}}':'홍길동', '{{사업명}}':'OO기관 네트워크 구축 사업',
  '{{견적합계_한글_VAT포함}}':'일금 삼천사백구십팔만원정', '{{견적합계_한글_VAT미포함}}':'일금 삼천백팔십만원정',
  '{{견적합계_숫자_VAT포함}}':'₩34,980,000', '{{견적합계_숫자_VAT미포함}}':'₩31,800,000',
  '{{견적년월일}}':'2026년 05월 16일', '{{견적유효기간}}':'2026년 06월 16일',
  '{{최종사용자}}':'한국통신 주식회사', '{{견적번호}}':'INT-260516-KCS-0001',
  '{{결제조건}}':'선금 50%, 납품 후 잔금 50%', '{{납기}}':'발주 후 14일', '{{무상하자보증기간}}':'납품 후 1년',
  '{{비고}}':'본 견적 금액은 설치비 별도입니다.<br>설치 및 초기 세팅 지원이 포함되어 있습니다.',
  '{{담당자_이름}}':'김철수', '{{담당자_직급}}':'대리',
  '{{견적담당자_전화}}':'010-1234-5678', '{{견적담당자_메일}}':'kim@soosan.co.kr',
};

// --- Selection Manager ---
function useSelectionManager(editorRef) {
  const savedRange = useRef(null);
  const save = useCallback(() => {
    const sel = window.getSelection();
    if (sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  }, [editorRef]);
  const restore = useCallback(() => {
    if (!savedRange.current) return false;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(savedRange.current);
    return true;
  }, []);
  return { save, restore };
}

// --- Sanitize pasted HTML ---
function sanitizeHtml(html) {
  const el = document.createElement('div');
  el.innerHTML = html;
  // Remove scripts, event handlers
  el.querySelectorAll('script,style,link,meta').forEach(n => n.remove());
  el.querySelectorAll('*').forEach(n => {
    for (const attr of [...n.attributes]) {
      if (attr.name.startsWith('on')) n.removeAttribute(attr.name);
    }
  });
  return el.innerHTML;
}

export default function HtmlEditor({ value, onChange, label, onSave }) {
  const [mode, setMode] = useState('gui');
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const composingRef = useRef(false);
  const internalUpdate = useRef(false);
  const { save: saveSel, restore: restoreSel } = useSelectionManager(editorRef);

  // UI panels
  const [showVarMenu, setShowVarMenu] = useState(false);
  const [tableInfo, setTableInfo] = useState(null); // { table, row, col }
  const [tableStyle, setTableStyle] = useState({ color:'#d1d5db', width:'1', type:'solid' });
  const [cellPadding, setCellPadding] = useState('6');
  const [cellWidth, setCellWidth] = useState('');
  const [selectedCells, setSelectedCells] = useState([]); // [{row,col}]
  const [imgEl, setImgEl] = useState(null);
  const [imgSize, setImgSize] = useState({ w:'', h:'' });
  const [showPreview, setShowPreview] = useState(false);

  // --- Sync: DOM → external state ---
  const syncContent = useCallback(() => {
    if (composingRef.current || !editorRef.current) return;
    // data-selected 속성 제거 후 HTML 추출
    const marked = editorRef.current.querySelectorAll('[data-selected]');
    marked.forEach(el => el.removeAttribute('data-selected'));
    const html = editorRef.current.innerHTML;
    marked.forEach(el => el.setAttribute('data-selected', '1'));
    internalUpdate.current = true;
    onChange(html);
  }, [onChange]);

  // --- External value → DOM (only on mode switch or initial load) ---
  useEffect(() => {
    if (mode === 'gui' && editorRef.current && !internalUpdate.current) {
      editorRef.current.innerHTML = value || '';
    }
    internalUpdate.current = false;
  }, [mode]);

  // Initial load
  useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = value || '';
  }, []);

  // --- execCommand wrapper ---
  const exec = useCallback((cmd, val) => {
    restoreSel();
    document.execCommand(cmd, false, val);
    syncContent();
  }, [restoreSel, syncContent]);

  // --- Prevent selection loss on toolbar buttons ---
  const tb = (e) => { e.preventDefault(); saveSel(); };

  // --- Font size: save sel → type pt → Enter → apply ---
  const fontSizeRef = useRef(null);
  const applyFontSize = useCallback(() => {
    const pt = fontSizeRef.current?.value;
    if (!pt) return;
    restoreSel();
    document.execCommand('fontSize', false, '7');
    editorRef.current?.querySelectorAll('font[size="7"]').forEach(f => {
      const span = document.createElement('span');
      span.style.fontSize = pt + 'pt';
      span.innerHTML = f.innerHTML;
      f.replaceWith(span);
    });
    syncContent();
    fontSizeRef.current.value = '';
  }, [restoreSel, syncContent]);

  // --- Paste handler ---
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    if (html) {
      document.execCommand('insertHTML', false, sanitizeHtml(html));
    } else {
      document.execCommand('insertText', false, text);
    }
    syncContent();
  }, [syncContent]);

  // --- Variable insertion ---
  const insertVar = (v) => {
    restoreSel();
    document.execCommand('insertText', false, v.value);
    syncContent();
    setShowVarMenu(false);
  };

  // --- Table insertion ---
  const insertTable = () => {
    const rows = prompt('행 수', '3');
    const cols = prompt('열 수', '2');
    if (!rows || !cols) return;
    let html = '<table style="width:100%;border-collapse:collapse;border:1px solid #d1d5db;margin:8px 0">';
    for (let r = 0; r < parseInt(rows); r++) {
      html += '<tr>';
      for (let c = 0; c < parseInt(cols); c++) html += '<td style="border:1px solid #d1d5db;padding:6px 8px">&nbsp;</td>';
      html += '</tr>';
    }
    html += '</table>';
    restoreSel();
    document.execCommand('insertHTML', false, html);
    syncContent();
  };

  // --- Image upload ---
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_ORIGIN}/api/upload`, { method:'POST', headers:{'Authorization':`Bearer ${token}`}, body:formData });
      const data = await res.json();
      if (data.url) {
        restoreSel();
        document.execCommand('insertHTML', false, `<img src="${data.url}" style="max-width:100%;height:auto" />`);
        syncContent();
      } else alert(data.error || '업로드 실패');
    } catch(err) { alert('업로드 실패: ' + err.message); }
    e.target.value = '';
  };

  // --- Table drag resize ---
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || mode !== 'gui') return;
    let resizing = null;

    const onMouseMove = (e) => {
      if (resizing) {
        e.preventDefault();
        if (resizing.type === 'col') {
          const w = Math.max(20, resizing.startSize + e.clientX - resizing.startPos);
          resizing.table.querySelectorAll('tr').forEach(row => { const c = row.querySelectorAll('td,th')[resizing.index]; if (c) c.style.width = w + 'px'; });
        } else {
          const h = Math.max(16, resizing.startSize + e.clientY - resizing.startPos);
          const rows = resizing.table.querySelectorAll('tr');
          if (rows[resizing.index]) rows[resizing.index].style.height = h + 'px';
        }
        return;
      }
      const td = e.target.closest('td,th');
      if (!td || !editor.contains(td)) { editor.style.cursor = ''; return; }
      const rect = td.getBoundingClientRect();
      editor.style.cursor = Math.abs(e.clientX - rect.right) < 5 ? 'col-resize' : Math.abs(e.clientY - rect.bottom) < 5 ? 'row-resize' : '';
    };

    const onMouseDown = (e) => {
      const td = e.target.closest('td,th');
      if (!td || !editor.contains(td)) return;
      const table = td.closest('table');
      if (!table) return;
      const rect = td.getBoundingClientRect();
      if (Math.abs(e.clientX - rect.right) < 5) {
        e.preventDefault();
        const colIdx = Array.from(td.parentElement.querySelectorAll('td,th')).indexOf(td);
        resizing = { type:'col', table, index:colIdx, startPos:e.clientX, startSize:td.offsetWidth };
      } else if (Math.abs(e.clientY - rect.bottom) < 5) {
        e.preventDefault();
        const rowIdx = Array.from(table.querySelectorAll('tr')).indexOf(td.parentElement);
        resizing = { type:'row', table, index:rowIdx, startPos:e.clientY, startSize:td.parentElement.offsetHeight };
      }
    };

    const onMouseUp = () => { if (resizing) { resizing = null; syncContent(); } };

    editor.addEventListener('mousemove', onMouseMove);
    editor.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      editor.removeEventListener('mousemove', onMouseMove);
      editor.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [mode, syncContent]);

  // --- Editor click: table/image selection ---
  const handleEditorClick = (e) => {
    // Image
    if (e.target.tagName === 'IMG') {
      editorRef.current?.querySelectorAll('img').forEach(i => { i.removeAttribute('data-selected'); });
      e.target.setAttribute('data-selected','1');
      setImgEl(e.target);
      setImgSize({ w: parseInt(e.target.style.width || e.target.naturalWidth) || '', h: parseInt(e.target.style.height) || '' });
      setTableInfo(null); setSelectedCells([]);
      return;
    }
    if (imgEl) { imgEl.removeAttribute('data-selected'); setImgEl(null); }

    // Table/cell
    const td = e.target.closest('td,th');
    if (td) {
      const table = td.closest('table');
      if (table && editorRef.current?.contains(table)) {
        const rows = Array.from(table.querySelectorAll('tr'));
        const row = td.parentElement;
        const rowIdx = rows.indexOf(row);
        const colIdx = Array.from(row.children).indexOf(td);

        // Parse border
        const border = table.style.border || '1px solid #d1d5db';
        const m = border.match(/(\d+)px\s+(solid|dashed|dotted|none)\s+(#?\w+)/);
        if (m) setTableStyle({ width:m[1], type:m[2], color:m[3] });

        // Clear highlights
        table.querySelectorAll('td,th').forEach(c => { c.removeAttribute('data-selected'); });

        if (e.shiftKey && selectedCells.length > 0 && tableInfo?.table === table) {
          // Range select
          const sr = Math.min(selectedCells[0].row, rowIdx), er = Math.max(selectedCells[0].row, rowIdx);
          const sc = Math.min(selectedCells[0].col, colIdx), ec = Math.max(selectedCells[0].col, colIdx);
          const cells = [];
          for (let r = sr; r <= er; r++) for (let c = sc; c <= ec; c++) {
            const cell = rows[r]?.children[c];
            if (cell) { cell.setAttribute('data-selected','1'); cells.push({row:r,col:c}); }
          }
          setSelectedCells(cells);
        } else {
          td.setAttribute('data-selected','1');
          setSelectedCells([{row:rowIdx, col:colIdx}]);
          setCellPadding(String(parseInt(td.style.padding) || 6));
          setCellWidth(String(parseInt(td.style.width) || td.offsetWidth || ''));
        }
        setTableInfo({ table, row: rowIdx, col: colIdx });
        return;
      }
    }
    // Deselect
    if (tableInfo?.table) tableInfo.table.querySelectorAll('td,th').forEach(c => { c.removeAttribute('data-selected'); });
    setTableInfo(null); setSelectedCells([]);
  };

  // --- Table helpers (coordinate-based, no stale refs) ---
  const getCell = (r, c) => tableInfo?.table?.querySelectorAll('tr')[r]?.children[c] || null;
  const forSelectedCells = (fn) => selectedCells.forEach(({row,col}) => { const c = getCell(row,col); if(c) fn(c,row,col); });

  const applyTableBorder = () => {
    if (!tableInfo?.table) return;
    const val = tableStyle.type === 'none' ? 'none' : `${tableStyle.width}px ${tableStyle.type} ${tableStyle.color}`;
    tableInfo.table.style.border = val;
    tableInfo.table.querySelectorAll('td,th').forEach(c => { c.style.border = val; });
    syncContent();
  };

  const applyCellPad = (val) => {
    setCellPadding(val);
    if (selectedCells.length > 0) forSelectedCells(c => { c.style.padding = val + 'px'; });
    else if (tableInfo?.table) tableInfo.table.querySelectorAll('td,th').forEach(c => { c.style.padding = val + 'px'; });
    syncContent();
  };

  const addRow = (pos) => {
    if (selectedCells.length !== 1 || !tableInfo?.table) return;
    const rows = tableInfo.table.querySelectorAll('tr');
    const ref = rows[selectedCells[0].row];
    if (!ref) return;
    const newRow = document.createElement('tr');
    const bdr = ref.children[0]?.style.border || '1px solid #d1d5db';
    const pad = ref.children[0]?.style.padding || '6px 8px';
    for (let c = 0; c < ref.children.length; c++) {
      const td = document.createElement('td');
      td.style.border = bdr; td.style.padding = pad; td.innerHTML = '&nbsp;';
      newRow.appendChild(td);
    }
    if (pos === 'above') ref.before(newRow); else ref.after(newRow);
    syncContent();
  };

  const addCol = (pos) => {
    if (selectedCells.length !== 1 || !tableInfo?.table) return;
    const ci = selectedCells[0].col;
    tableInfo.table.querySelectorAll('tr').forEach(row => {
      const ref = row.children[ci];
      if (!ref) return;
      const td = document.createElement('td');
      td.style.border = ref.style.border || '1px solid #d1d5db';
      td.style.padding = ref.style.padding || '6px 8px';
      td.innerHTML = '&nbsp;';
      if (pos === 'left') ref.before(td); else ref.after(td);
    });
    syncContent();
  };

  const deleteRow = () => {
    if (selectedCells.length !== 1 || !tableInfo?.table) return;
    const rows = tableInfo.table.querySelectorAll('tr');
    if (rows.length <= 1) return;
    rows[selectedCells[0].row]?.remove();
    setSelectedCells([]); syncContent();
  };

  const deleteCol = () => {
    if (selectedCells.length !== 1 || !tableInfo?.table) return;
    const first = tableInfo.table.querySelector('tr');
    if (first && first.children.length <= 1) return;
    tableInfo.table.querySelectorAll('tr').forEach(row => { row.children[selectedCells[0].col]?.remove(); });
    setSelectedCells([]); syncContent();
  };

  const mergeCells = () => {
    if (selectedCells.length < 2 || !tableInfo?.table) return;
    const rows = [...new Set(selectedCells.map(c=>c.row))].sort((a,b)=>a-b);
    const cols = [...new Set(selectedCells.map(c=>c.col))].sort((a,b)=>a-b);
    const first = getCell(rows[0], cols[0]);
    if (!first) return;
    const contents = selectedCells.map(({row,col})=>getCell(row,col)?.innerHTML?.trim()).filter(h=>h&&h!=='&nbsp;');
    first.innerHTML = contents.join(' ') || '&nbsp;';
    if (rows.length > 1) first.setAttribute('rowspan', rows.length);
    if (cols.length > 1) first.setAttribute('colspan', cols.length);
    selectedCells.forEach(({row,col})=>{const c=getCell(row,col);if(c&&c!==first)c.remove();});
    tableInfo.table.querySelectorAll('td,th').forEach(c=>{c.style.outline='';});
    setSelectedCells([{row:rows[0],col:cols[0]}]);
    first.setAttribute('data-selected','1');
    syncContent();
  };

  const unmergeCells = () => {
    if (selectedCells.length !== 1 || !tableInfo?.table) return;
    const cell = getCell(selectedCells[0].row, selectedCells[0].col);
    if (!cell) return;
    const rs = parseInt(cell.getAttribute('rowspan'))||1, cs = parseInt(cell.getAttribute('colspan'))||1;
    if (rs===1 && cs===1) return;
    const rows = Array.from(tableInfo.table.querySelectorAll('tr'));
    const bdr = cell.style.border||'1px solid #d1d5db', pad = cell.style.padding||'6px 8px';
    cell.removeAttribute('rowspan'); cell.removeAttribute('colspan');
    for (let c=1;c<cs;c++){const td=document.createElement('td');td.style.border=bdr;td.style.padding=pad;td.innerHTML='&nbsp;';cell.after(td);}
    for (let r=1;r<rs;r++){const row=rows[selectedCells[0].row+r];if(!row)continue;const ref=row.children[selectedCells[0].col]||null;for(let c=0;c<cs;c++){const td=document.createElement('td');td.style.border=bdr;td.style.padding=pad;td.innerHTML='&nbsp;';if(ref)row.insertBefore(td,ref);else row.appendChild(td);}}
    tableInfo.table.querySelectorAll('td,th').forEach(c=>{c.style.outline='';});
    setSelectedCells([]); syncContent();
  };

  // --- Image helpers ---
  const applyImgSize = (w, h) => { if (!imgEl) return; if (w) imgEl.style.width=w+'px'; if (h) imgEl.style.height=h+'px'; syncContent(); };
  const applyImgPct = (p) => { if (!imgEl) return; imgEl.style.width=p+'%'; imgEl.style.height='auto'; setImgSize({w:p+'%',h:'auto'}); syncContent(); };
  const deleteImg = () => { if (!imgEl) return; imgEl.remove(); setImgEl(null); syncContent(); };

  // --- Preview ---
  const getPreviewHtml = () => {
    let html = value || '';
    Object.entries(PREVIEW_SAMPLE).forEach(([k,v])=>{html=html.replaceAll(k,v);});
    TEMPLATE_VARS.forEach(tv=>{html=html.replaceAll(tv.value, PREVIEW_SAMPLE[tv.value]||tv.value);});
    return html;
  };

  // --- Styles ---
  const btn = { padding:'3px 8px', border:'1px solid #d1d5db', background:'#fff', borderRadius:4, fontSize:11, cursor:'pointer' };
  const sep = <span style={{width:1,height:18,background:'#d1d5db',margin:'0 4px'}}/>;

  return (
    <div className="card">
      <style>{`[data-selected] { outline: 2px solid #3b82f6 !important; }`}</style>
      <div className="card-header">
        <div className="card-title">{label}</div>
        <div className="btn-group">
          <button className={`btn btn-sm ${mode==='gui'?'btn-primary':'btn-outline'}`} onClick={()=>setMode('gui')}>GUI</button>
          <button className={`btn btn-sm ${mode==='html'?'btn-primary':'btn-outline'}`} onClick={()=>setMode('html')}>HTML</button>
          <button className="btn btn-outline btn-sm" onClick={()=>setShowPreview(true)}>미리보기</button>
        </div>
      </div>

      {mode === 'gui' ? (
        <>
          {/* Toolbar */}
          <div style={{display:'flex',gap:2,padding:6,background:'#f8fafc',border:'1px solid #e2e8f0',borderBottom:'none',borderRadius:'7px 7px 0 0',flexWrap:'wrap',alignItems:'center'}}>
            <button style={btn} onMouseDown={tb} onClick={()=>exec('bold')} title="굵게"><b>B</b></button>
            <button style={btn} onMouseDown={tb} onClick={()=>exec('italic')} title="기울임"><i>I</i></button>
            <button style={btn} onMouseDown={tb} onClick={()=>exec('underline')} title="밑줄"><u>U</u></button>
            <button style={btn} onMouseDown={tb} onClick={()=>exec('strikeThrough')} title="취소선"><s>S</s></button>
            {sep}
            <label style={{display:'flex',alignItems:'center',gap:2,fontSize:11}}>
              <input ref={fontSizeRef} type="number" min="6" max="72" placeholder="pt" style={{width:42,padding:'2px 4px',border:'1px solid #d1d5db',borderRadius:3,fontSize:11,textAlign:'center'}}
                onMouseDown={e=>{e.preventDefault();saveSel();e.target.focus();}}
                onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();applyFontSize();}}}
              />pt
              <button style={{...btn,fontSize:9,padding:'2px 5px'}} onMouseDown={tb} onClick={applyFontSize}>적용</button>
            </label>
            <input type="color" style={{width:24,height:22,border:'1px solid #d1d5db',borderRadius:3,cursor:'pointer',padding:0}} onMouseDown={tb} onInput={e=>exec('foreColor',e.target.value)} title="글자색" />
            <input type="color" style={{width:24,height:22,border:'1px solid #d1d5db',borderRadius:3,cursor:'pointer',padding:0}} onMouseDown={tb} onInput={e=>exec('hiliteColor',e.target.value)} title="배경색" defaultValue="#ffffff" />
            {sep}
            <button style={btn} onMouseDown={tb} onClick={()=>exec('justifyLeft')} title="왼쪽 정렬">≡←</button>
            <button style={btn} onMouseDown={tb} onClick={()=>exec('justifyCenter')} title="가운데 정렬">≡</button>
            <button style={btn} onMouseDown={tb} onClick={()=>exec('justifyRight')} title="오른쪽 정렬">≡→</button>
            {sep}
            <button style={btn} onMouseDown={tb} onClick={insertTable} title="표 삽입">표</button>
            <button style={btn} onMouseDown={tb} onClick={()=>fileInputRef.current?.click()} title="이미지">이미지</button>
            <input ref={fileInputRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleImageUpload} />
            <button style={btn} onMouseDown={tb} onClick={()=>exec('insertHorizontalRule')} title="구분선">━</button>
            {sep}
            <div style={{position:'relative',display:'inline-block'}}>
              <button style={{...btn,background:'#dbeafe',color:'#2563eb',borderColor:'#93c5fd',fontWeight:600}} onMouseDown={tb} onClick={()=>setShowVarMenu(!showVarMenu)}>✚ 변수 삽입 ▼</button>
              {showVarMenu && (
                <div style={{position:'absolute',top:'100%',left:0,background:'#fff',border:'1px solid #e2e8f0',borderRadius:8,boxShadow:'0 4px 16px rgba(0,0,0,0.12)',zIndex:60,minWidth:220,maxHeight:300,overflowY:'auto',marginTop:4}}>
                  {TEMPLATE_VARS.map(v=>(
                    <div key={v.value} onMouseDown={e=>e.preventDefault()} onClick={()=>insertVar(v)} style={{padding:'7px 12px',fontSize:12,cursor:'pointer',borderBottom:'1px solid #f1f5f9'}}
                      onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <span style={{color:'#3b82f6',fontWeight:600}}>{v.value}</span><br/><span style={{color:'#94a3b8',fontSize:11}}>{v.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Table panel */}
          {tableInfo && (
            <div style={{display:'flex',gap:10,padding:'8px 12px',background:'#fffbeb',border:'1px solid #fde68a',borderBottom:'none',alignItems:'center',fontSize:11,flexWrap:'wrap'}}>
              <span style={{fontWeight:700,color:'#92400e'}}>표:</span>
              <label style={{display:'flex',alignItems:'center',gap:3}}>테두리 <input type="color" value={tableStyle.color} onChange={e=>setTableStyle({...tableStyle,color:e.target.value})} style={{width:20,height:16,border:'1px solid #d1d5db',borderRadius:2,padding:0,cursor:'pointer'}} /></label>
              <select value={tableStyle.width} onChange={e=>setTableStyle({...tableStyle,width:e.target.value})} style={{padding:'1px 3px',border:'1px solid #d1d5db',borderRadius:3,fontSize:10}}>{[1,2,3,4,5].map(n=><option key={n} value={n}>{n}px</option>)}</select>
              <select value={tableStyle.type} onChange={e=>setTableStyle({...tableStyle,type:e.target.value})} style={{padding:'1px 3px',border:'1px solid #d1d5db',borderRadius:3,fontSize:10}}>
                <option value="solid">실선</option><option value="dashed">점선</option><option value="dotted">도트</option><option value="none">투명</option>
              </select>
              <button style={{...btn,fontSize:9,background:'#fef3c7',borderColor:'#fde68a'}} onClick={applyTableBorder}>적용</button>
              {sep}
              <label style={{display:'flex',alignItems:'center',gap:3}}>셀여백 <input type="number" min="0" max="40" value={cellPadding} onChange={e=>applyCellPad(e.target.value)} style={{width:34,padding:'1px 3px',border:'1px solid #d1d5db',borderRadius:3,fontSize:10,textAlign:'center'}} />px</label>
              <label style={{display:'flex',alignItems:'center',gap:3}}>셀배경 <input type="color" defaultValue="#ffffff" onChange={e=>{forSelectedCells(c=>{c.style.backgroundColor=e.target.value;});syncContent();}} style={{width:20,height:16,border:'1px solid #d1d5db',borderRadius:2,padding:0,cursor:'pointer'}} /></label>
              {sep}
              {[['left','←'],['center','≡'],['right','→']].map(([a,i])=><button key={a} style={{...btn,fontSize:9}} onClick={()=>{forSelectedCells(c=>{c.style.textAlign=a;});syncContent();}}>{i}</button>)}
              {[['top','↑'],['middle','─'],['bottom','↓']].map(([a,i])=><button key={a} style={{...btn,fontSize:9}} onClick={()=>{forSelectedCells(c=>{c.style.verticalAlign=a;});syncContent();}}>{i}</button>)}
              {sep}
              <button style={{...btn,fontSize:9}} onClick={()=>addRow('above')}>↑행</button>
              <button style={{...btn,fontSize:9}} onClick={()=>addRow('below')}>↓행</button>
              <button style={{...btn,fontSize:9}} onClick={()=>addCol('left')}>←열</button>
              <button style={{...btn,fontSize:9}} onClick={()=>addCol('right')}>→열</button>
              {sep}
              <button style={{...btn,fontSize:9,background:'#dbeafe',borderColor:'#93c5fd',color:'#2563eb'}} onClick={mergeCells}>병합</button>
              <button style={{...btn,fontSize:9}} onClick={unmergeCells}>해제</button>
              {selectedCells.length > 1 && <span style={{color:'#2563eb',fontSize:10}}>({selectedCells.length}셀)</span>}
              {sep}
              <button style={{...btn,fontSize:9,color:'#ef4444',borderColor:'#fecaca'}} onClick={deleteRow}>행삭제</button>
              <button style={{...btn,fontSize:9,color:'#ef4444',borderColor:'#fecaca'}} onClick={deleteCol}>열삭제</button>
              {selectedCells.length === 1 && <label style={{display:'flex',alignItems:'center',gap:3}}>열너비 <input type="number" min="20" max="680" value={cellWidth} onChange={e=>{
                const w = e.target.value; setCellWidth(w);
                if (!w || !tableInfo?.table) return;
                const ci = selectedCells[0].col;
                tableInfo.table.querySelectorAll('tr').forEach(row=>{const c=row.querySelectorAll('td,th')[ci];if(c)c.style.width=w+'px';});
                syncContent();
              }} style={{width:50,padding:'1px 3px',border:'1px solid #d1d5db',borderRadius:3,fontSize:10,textAlign:'center'}} />px <span style={{color:'#94a3b8',fontSize:9}}>(최대 680)</span></label>}
            </div>
          )}

          {/* Image panel */}
          {imgEl && (
            <div style={{display:'flex',gap:10,padding:'8px 12px',background:'#eff6ff',border:'1px solid #bfdbfe',borderBottom:'none',alignItems:'center',fontSize:11,flexWrap:'wrap'}}>
              <span style={{fontWeight:700,color:'#1e40af'}}>이미지:</span>
              <label style={{display:'flex',alignItems:'center',gap:3}}>너비 <input type="number" min="10" value={parseInt(imgSize.w)||''} onChange={e=>{setImgSize({...imgSize,w:e.target.value});applyImgSize(e.target.value,null);}} style={{width:55,padding:'1px 3px',border:'1px solid #d1d5db',borderRadius:3,fontSize:10,textAlign:'center'}} />px</label>
              <label style={{display:'flex',alignItems:'center',gap:3}}>높이 <input type="number" min="10" value={parseInt(imgSize.h)||''} onChange={e=>{setImgSize({...imgSize,h:e.target.value});applyImgSize(null,e.target.value);}} style={{width:55,padding:'1px 3px',border:'1px solid #d1d5db',borderRadius:3,fontSize:10,textAlign:'center'}} />px</label>
              {sep}
              {[25,50,75,100].map(p=><button key={p} onClick={()=>applyImgPct(p)} style={{padding:'2px 7px',border:'1px solid #93c5fd',background:'#dbeafe',borderRadius:4,fontSize:10,cursor:'pointer',color:'#2563eb'}}>{p}%</button>)}
              {sep}
              <button onClick={deleteImg} style={{padding:'2px 7px',border:'1px solid #fecaca',background:'#fef2f2',borderRadius:4,fontSize:10,cursor:'pointer',color:'#ef4444'}}>삭제</button>
            </div>
          )}

          {/* Editor */}
          <div ref={editorRef} contentEditable suppressContentEditableWarning
            onInput={syncContent}
            onPaste={handlePaste}
            onCompositionStart={()=>{composingRef.current=true;}}
            onCompositionEnd={()=>{composingRef.current=false;syncContent();}}
            onClick={handleEditorClick}
            onBlur={saveSel}
            style={{minHeight:200,padding:16,border:'1px solid #e2e8f0',borderRadius:'0 0 7px 7px',outline:'none',background:'#fff',fontSize:13,lineHeight:1.6}}
          />
        </>
      ) : mode === 'html' ? (
        <textarea className="form-input" rows={14} value={value} onChange={e=>onChange(e.target.value)} placeholder="HTML 코드를 직접 편집하세요..."
          style={{fontFamily:'Consolas,monospace',fontSize:12,lineHeight:1.5}} />
      ) : null}

      {onSave && (
        <div style={{display:'flex',justifyContent:'flex-end',padding:'8px 0'}}>
          <button className="btn btn-primary btn-sm" onClick={onSave}>💾 {label} 저장</button>
        </div>
      )}

      {/* Inline preview */}
      {showPreview && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#fff',borderRadius:14,padding:28,width:700,maxHeight:'85vh',overflowY:'auto'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
              <div style={{fontSize:15,fontWeight:700}}>미리보기</div>
              <span style={{cursor:'pointer',color:'#94a3b8'}} onClick={()=>setShowPreview(false)}>✕</span>
            </div>
            <div className="text-muted" style={{marginBottom:8}}>변수는 샘플 데이터로 대체됩니다.</div>
            <div style={{border:'1px solid #e2e8f0',borderRadius:8,padding:20,background:'#fff',fontSize:13,lineHeight:1.8}}
              dangerouslySetInnerHTML={{__html:getPreviewHtml()}} />
            <div style={{display:'flex',justifyContent:'flex-end',marginTop:16}}>
              <button className="btn btn-outline" onClick={()=>setShowPreview(false)}>닫기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
